import { useEffect, useCallback } from 'react';
import { useSSE } from '../app/providers/SseProvider';
import {
  INTERACTION_RECEIVED_SSE_EVENT,
  SESSION_STARTED_SSE_EVENT,
  SESSION_STOPPED_SSE_EVENT,
  type Interaction,
} from '@terra/shared-contracts-interact';

type InteractSSEEventType =
  | 'INTERACTION_RECEIVED'
  | 'SESSION_STARTED'
  | 'SESSION_STOPPED';

type InteractSSEEvent = {
  type: InteractSSEEventType;
  data: {
    sessionId: string;
    interaction?: Interaction;
    domain?: string;
    expiresAt?: string;
  };
};

type UseInteractSSEParams = {
  sessionId?: string;
  enabled?: boolean;
  onInteractionReceived?: (data: {
    sessionId: string;
    interaction: Interaction;
  }) => void;
  onSessionStarted?: (data: {
    sessionId: string;
    domain: string;
    expiresAt: string;
  }) => void;
  onSessionStopped?: (data: { sessionId: string }) => void;
};

export function useInteractSSE({
  sessionId,
  enabled = true,
  onInteractionReceived,
  onSessionStarted,
  onSessionStopped,
}: UseInteractSSEParams) {
  const { subscribe } = useSSE();

  const handleEvent = useCallback(
    (event: InteractSSEEvent) => {
      if (sessionId && event.data.sessionId !== sessionId) {
        return;
      }

      switch (event.type) {
        case 'INTERACTION_RECEIVED':
          if (onInteractionReceived && event.data.interaction) {
            onInteractionReceived({
              sessionId: event.data.sessionId,
              interaction: event.data.interaction,
            });
          }
          break;
        case 'SESSION_STARTED':
          if (onSessionStarted && event.data.domain && event.data.expiresAt) {
            onSessionStarted({
              sessionId: event.data.sessionId,
              domain: event.data.domain,
              expiresAt: event.data.expiresAt,
            });
          }
          break;
        case 'SESSION_STOPPED':
          if (onSessionStopped) {
            onSessionStopped({ sessionId: event.data.sessionId });
          }
          break;
      }
    },
    [sessionId, onInteractionReceived, onSessionStarted, onSessionStopped],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const unsubscribers = [
      subscribe(INTERACTION_RECEIVED_SSE_EVENT, (data) => {
        handleEvent({ type: 'INTERACTION_RECEIVED', data });
      }),
      subscribe(SESSION_STARTED_SSE_EVENT, (data) => {
        handleEvent({ type: 'SESSION_STARTED', data });
      }),
      subscribe(SESSION_STOPPED_SSE_EVENT, (data) => {
        handleEvent({ type: 'SESSION_STOPPED', data });
      }),
    ];

    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, [subscribe, handleEvent, enabled, sessionId]);
}
